import React from 'react';
import { flexRender, Table } from '@tanstack/react-table';
import { Empty } from 'antd';
import { ITableFilters } from '../../../types';
import {
  cn,
  getCommonColumnPinningStyles,
  getCommonColumnPinningStylesForHeader,
} from '../../../utils';
import InlineListPagination from './InlineListPagination';

export interface ICommonTravelTableProps<T, U extends ITableFilters> {
  table: Table<T>;
  total: number;
  appliedFilters: U;
  setAppliedFilters: React.Dispatch<React.SetStateAction<U>>;
  density?: 'standard' | 'comfortable';
  /** Use `none` when the owning page renders the shared toolbar paginator. */
  paginationPlacement?: 'bottom' | 'none';
}

const CommonTravelTable = <T, U extends ITableFilters>({
  table,
  total,
  appliedFilters,
  setAppliedFilters,
  density = 'standard',
  paginationPlacement = 'bottom',
}: ICommonTravelTableProps<T, U>) => {
  const isStandardDensity = density === 'standard';
  // TanStack keeps the table instance stable while updating its internal data.
  // Read the row model on every render so an API/cache response cannot leave the
  // table body displaying rows from the previous query arguments.
  const rowModel = table.getRowModel();
  const hasRows = rowModel.rows.length > 0;
  const showPagination = paginationPlacement === 'bottom' && total > 0;

  const scrollContainerStyle = React.useMemo(() => {
    if (!hasRows) return undefined;
    return {
      height: showPagination ? 'calc(100vh - 11.5rem)' : 'calc(100vh - 8.8rem)',
    };
  }, [hasRows, showPagination]);

  return (
    <div className="flex flex-col">
      <div
        style={scrollContainerStyle}
        className={cn('relative overflow-x-auto border border-slate-200', {
          'h-[calc(100vh-11.5rem)]': hasRows && showPagination,
          'h-[calc(100vh-8.8rem)]': hasRows && !showPagination,
          'h-[calc(100vh-7.5rem)]': !hasRows,
        })}
      >
        {hasRows && (
          <table
            className={cn(
              'min-w-full border-spacing-0',
              isStandardDensity
                ? 'text-xs text-slate-700'
                : 'text-sm text-slate-700',
            )}
          >
            <thead className="sticky top-0 z-[11] border-b border-slate-200 bg-slate-50">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      className={cn(
                        isStandardDensity
                          ? 'px-2.5 py-1.5 text-xs font-semibold text-left whitespace-nowrap first:pl-3 first:pr-3'
                          : 'px-4 py-2.5 text-xs font-semibold text-left whitespace-nowrap first:pl-3 first:pr-3',
                        'text-slate-600',
                      )}
                      style={getCommonColumnPinningStylesForHeader(
                        header.column,
                        table,
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody className="divide-y divide-slate-100 bg-white">
              {rowModel.rows.map(row => (
                <tr
                  key={row.id}
                  className="bg-white transition-colors duration-100 hover:bg-slate-50"
                >
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      className={cn(
                        isStandardDensity
                          ? 'px-2.5 py-1.5 text-xs text-slate-700 whitespace-nowrap truncate first:pl-3 first:pr-3'
                          : 'px-4 py-2.5 text-xs text-slate-700 whitespace-nowrap truncate first:pl-3 first:pr-3',
                        'font-medium tracking-normal',
                        'border-b border-slate-100',
                        'text-left',
                      )}
                      style={getCommonColumnPinningStyles(cell.column, table)}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!hasRows && (
          <div className="grid place-items-center h-full">
            <Empty />
          </div>
        )}
      </div>
      {showPagination && (
        <div className="flex min-h-11 items-center justify-end border-x border-b border-slate-200 bg-slate-50/70 px-2 py-1.5">
          <InlineListPagination
            total={total}
            filters={appliedFilters}
            setFilters={setAppliedFilters}
          />
        </div>
      )}
    </div>
  );
};

export default CommonTravelTable;
